import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig
} from 'axios';

export interface ApiError extends Error {
  status?: number;
  detail?: unknown;
  isUnauthorized?: boolean;
  isForbidden?: boolean;
  isReadOnly?: boolean;
  isServiceUnavailable?: boolean;
}

/** Extracts FastAPI's `{ detail }` (string or Pydantic validation array) into a readable `Error`. */
export function toApiError(error: unknown): ApiError {
  if (axios.isAxiosError(error)) {
    const axErr = error as AxiosError<{ detail?: unknown; message?: string }>;
    const status = axErr.response?.status;
    const data = axErr.response?.data;
    const detail = data?.detail ?? data?.message;
    let message: string;
    if (typeof detail === 'string' && detail.trim()) {
      message = detail;
    } else if (Array.isArray(detail)) {
      message = detail
        .map((d) => {
          if (typeof d === 'object' && d !== null) {
            const loc = Array.isArray((d as { loc?: unknown }).loc)
              ? (d as { loc: unknown[] }).loc.slice(1).join('.')
              : '';
            const msg = String((d as { msg?: unknown }).msg ?? '');
            return loc ? `${loc}: ${msg}` : msg;
          }
          return String(d);
        })
        .join('; ');
    } else if (status === 503) {
      message = 'Sign-in service unavailable; retry';
    } else {
      message =
        axErr.message || `Request failed (${status ?? 'network error'})`;
    }

    const wrapped: ApiError = new Error(message);
    wrapped.status = status;
    wrapped.detail = detail ?? data;
    wrapped.isUnauthorized = status === 401;
    wrapped.isForbidden = status === 403;
    wrapped.isServiceUnavailable = status === 503;
    wrapped.isReadOnly =
      status === 403 && message.toLowerCase().includes('read-only deployment');
    return wrapped;
  }
  if (error instanceof Error) return error as ApiError;
  return new Error(String(error));
}

/** HTTP status carried by an error from this API client (or any error shaped like one), if any. */
export function getApiErrorStatus(error: unknown): number | undefined {
  const status = (error as { status?: unknown } | null | undefined)?.status;
  return typeof status === 'number' ? status : undefined;
}

/** On only when VITE_MARKETPLACE_INTELLIGENCE_USE_MOCK=true. Otherwise the API. */
export const MARKET_INTELLIGENCE_USE_MOCK_DATA =
  import.meta.env.VITE_MARKETPLACE_INTELLIGENCE_USE_MOCK === 'true';

/** Mock data is loaded on demand, so it stays out of the bundle unless the flag is on. */
export const loadMarketIntelligenceMock = () =>
  import('../studies/market-intelligence-mock');

export class MarketplaceIntelligenceBaseApiClient {
  protected readonly client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this.client = client;
  }

  protected async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const r = await this.client.get<T>(url, config);
    return r.data;
  }

  protected async post<T>(
    url: string,
    body?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const r = await this.client.post<T>(url, body, config);
    return r.data;
  }

  protected async patch<T>(
    url: string,
    body?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const r = await this.client.patch<T>(url, body, config);
    return r.data;
  }

  protected async put<T>(
    url: string,
    body?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const r = await this.client.put<T>(url, body, config);
    return r.data;
  }

  protected async delete<T = void>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const r = await this.client.delete<T>(url, config);
    return r.data;
  }
}

export interface CreateMarketplaceIntelligenceHttpClientOptions {
  baseURL?: string;
  crmEnvironment?: string;
  getToken?: () => string | null;
  onUnauthorized?: (error: ApiError) => void;
}

let unauthorizedInFlight = false;

/** Resets single-flight 401 guard (used after re-login or in unit tests). */
export function resetMarketplaceIntelligenceUnauthorizedState(): void {
  unauthorizedInFlight = false;
}

/**
 * Second Axios instance dedicated to the Marketplace Intelligence FastAPI backend (`VITE_MARKETPLACE_INTELLIGENCE_API_URL`).
 *
 * - Attaches `Authorization: Bearer <CRM token>` from `localStorage.getItem('token')`.
 * - Optionally attaches `X-CRM-Environment` when `VITE_MARKETPLACE_INTELLIGENCE_CRM_ENV` is configured.
 * - Never sends `withCredentials`, `X-XSRF-TOKEN`, or `Version` headers.
 * - A 401 rejects the request and does not log the user out of the CRM portal. Pass `onUnauthorized` to observe it.
 * - 503 is retryable and 403 is a scope or read-only response. Neither logs the user out.
 */
export function createMarketplaceIntelligenceHttpClient(
  options: CreateMarketplaceIntelligenceHttpClientOptions = {}
): AxiosInstance {
  const baseURL =
    options.baseURL ??
    import.meta.env.VITE_MARKETPLACE_INTELLIGENCE_API_URL ??
    '';
  const crmEnv =
    options.crmEnvironment ??
    import.meta.env.VITE_MARKETPLACE_INTELLIGENCE_CRM_ENV ??
    '';
  const getToken =
    options.getToken ??
    (() =>
      typeof localStorage !== 'undefined'
        ? localStorage.getItem('token')
        : null);
  const http = axios.create({
    baseURL,
    timeout: 60_000,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
  });

  http.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      unauthorizedInFlight = false;
    }
    if (crmEnv) {
      config.headers['X-CRM-Environment'] = crmEnv;
    }
    return config;
  });

  http.interceptors.response.use(
    (res) => res,
    (err: unknown) => {
      const apiError = toApiError(err);

      if (
        apiError.status === 401 &&
        options.onUnauthorized &&
        !unauthorizedInFlight
      ) {
        unauthorizedInFlight = true;
        options.onUnauthorized(apiError);
      }

      return Promise.reject(apiError);
    }
  );

  return http;
}

/**
 * Downloads the generated DOCX report for a study via the authenticated Marketplace Intelligence Axios instance
 * (`Authorization: Bearer <CRM token>`, never a `?token=` query string).
 */
export async function downloadStudyDocxWithClient(
  http: AxiosInstance,
  studyId: string
): Promise<void> {
  const res = await http.get<Blob>(
    `/api/v1/studies/${encodeURIComponent(studyId)}/report/docx`,
    { responseType: 'blob' }
  );
  const disposition = String(res.headers['content-disposition'] ?? '');
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  const filename = match?.[1] ?? `study-${studyId}-report.docx`;

  const url = URL.createObjectURL(res.data);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
